// FORFEO INC. - Lot 4R-D
// Contrat pur des politiques commerciales Forfeo Reserve.
// Reserve sélectionne et fige les règles métier; Giftshop résout tout effet financier.

import {
  FORFEO_RESERVE_AUTHORITIES,
  hasForfeoReserveForbiddenClientFields,
  normalizeForfeoReserveIdempotencyKey,
} from './forfeo-reserve-availability.ts';

export const FORFEO_RESERVE_POLICY_CONTRACT_VERSION =
  'forfeo-reserve-commercial-policy-4r-d-v1' as const;

export const FORFEO_RESERVE_POLICY_SCOPES = [
  'establishment',
  'service',
  'resource',
  'channel',
  'merchant_plan',
] as const;

export type ForfeoReservePolicyScope =
  (typeof FORFEO_RESERVE_POLICY_SCOPES)[number];

export const FORFEO_RESERVE_CONFIRMATION_MODES = [
  'free_booking',
  'pay_on_site',
  'deposit_required',
  'full_payment_required',
  'preauthorization_required',
] as const;

export type ForfeoReserveConfirmationMode =
  (typeof FORFEO_RESERVE_CONFIRMATION_MODES)[number];

export const FORFEO_RESERVE_POLICY_STATES = [
  'draft',
  'active',
  'superseded',
  'archived',
] as const;

export type ForfeoReservePolicyState =
  (typeof FORFEO_RESERVE_POLICY_STATES)[number];

export const FORFEO_RESERVE_BOOKING_CHANNELS = [
  'any',
  'direct',
  'explore',
  'partner_widget',
  'mobile',
  'merchant',
] as const;

export type ForfeoReserveBookingChannel =
  (typeof FORFEO_RESERVE_BOOKING_CHANNELS)[number];

export const FORFEO_RESERVE_POLICY_EFFECTS = [
  'none',
  'merchant_review',
  'giftshop_refund_review',
  'giftshop_credit_review',
  'non_refundable_review',
] as const;

export type ForfeoReservePolicyEffect =
  (typeof FORFEO_RESERVE_POLICY_EFFECTS)[number];

export const FORFEO_RESERVE_POLICY_COMMANDS = [
  'upsert_policy',
  'activate_policy',
  'archive_policy',
] as const;

export type ForfeoReservePolicyCommandKind =
  (typeof FORFEO_RESERVE_POLICY_COMMANDS)[number];

export interface ForfeoReserveCommercialPolicySelectors {
  catalogItemId?: string;
  resourceId?: string;
  channel: ForfeoReserveBookingChannel;
  merchantPlanCode?: string;
}

export interface ForfeoReserveCommercialPolicyContract {
  id: string;
  businessId: string;
  label: string;
  state: ForfeoReservePolicyState;
  confirmationMode: ForfeoReserveConfirmationMode;
  selectors: ForfeoReserveCommercialPolicySelectors;
  cancellationWindowMinutes: number;
  modificationWindowMinutes: number;
  lateCancellationEffect: ForfeoReservePolicyEffect;
  noShowEffect: ForfeoReservePolicyEffect;
  financialPolicyReference?: string;
  cancellationPolicyReference?: string;
  creditPolicyReference?: string;
  priority: number;
  validFrom?: string;
  validUntil?: string;
  revision: number;
  createdAt: string;
  updatedAt: string;
}

export interface ForfeoReserveResolvedCommercialPolicy {
  authority: 'RESERVE';
  policy?: ForfeoReserveCommercialPolicyContract;
  resolutionState: 'resolved' | 'manual_review_required';
  selectedScopes: readonly ForfeoReservePolicyScope[];
  confirmationMode?: ForfeoReserveConfirmationMode;
  financialHandoffRequired: boolean;
  cancellationDeadline?: string;
  modificationDeadline?: string;
  evaluatedAt: string;
  correlationId: string;
}

export interface ForfeoReservePolicySnapshot {
  authority: 'RESERVE';
  businessId: string;
  policies: readonly ForfeoReserveCommercialPolicyContract[];
  generatedAt: string;
}

export interface ForfeoReservePolicyResolutionRequest {
  businessId: string;
  catalogItemId: string;
  resourceId?: string;
  channel: ForfeoReserveBookingChannel;
  merchantPlanCode?: string;
  startsAt: string;
  correlationId: string;
}

export interface ForfeoReservePolicyCommand {
  kind: ForfeoReservePolicyCommandKind;
  businessId: string;
  policyId?: string;
  expectedRevision?: number;
  policy?: {
    label: string;
    confirmationMode: ForfeoReserveConfirmationMode;
    selectors: ForfeoReserveCommercialPolicySelectors;
    cancellationWindowMinutes: number;
    modificationWindowMinutes: number;
    lateCancellationEffect: ForfeoReservePolicyEffect;
    noShowEffect: ForfeoReservePolicyEffect;
    financialPolicyReference?: string;
    cancellationPolicyReference?: string;
    creditPolicyReference?: string;
    priority: number;
    validFrom?: string;
    validUntil?: string;
  };
  idempotencyKey: string;
  correlationId: string;
}

const UUID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const REFERENCE = /^[A-Za-z0-9][A-Za-z0-9:._-]{0,199}$/;
const PLAN_CODE = /^[A-Za-z0-9][A-Za-z0-9_-]{0,99}$/;

export function isForfeoReserveConfirmationMode(
  value: unknown,
): value is ForfeoReserveConfirmationMode {
  return FORFEO_RESERVE_CONFIRMATION_MODES.includes(
    value as ForfeoReserveConfirmationMode,
  );
}

export function isForfeoReserveBookingChannel(
  value: unknown,
): value is ForfeoReserveBookingChannel {
  return FORFEO_RESERVE_BOOKING_CHANNELS.includes(
    value as ForfeoReserveBookingChannel,
  );
}

export function isForfeoReservePolicyEffect(
  value: unknown,
): value is ForfeoReservePolicyEffect {
  return FORFEO_RESERVE_POLICY_EFFECTS.includes(
    value as ForfeoReservePolicyEffect,
  );
}

export function isForfeoReservePolicyCommandKind(
  value: unknown,
): value is ForfeoReservePolicyCommandKind {
  return FORFEO_RESERVE_POLICY_COMMANDS.includes(
    value as ForfeoReservePolicyCommandKind,
  );
}

export function forfeoReservePolicyRequiresGiftshop(
  mode: ForfeoReserveConfirmationMode,
): boolean {
  return [
    'deposit_required',
    'full_payment_required',
    'preauthorization_required',
  ].includes(mode);
}

export function calculateForfeoReservePolicySpecificity(
  selectors: ForfeoReserveCommercialPolicySelectors,
): number {
  return (
    (selectors.resourceId ? 100 : 0)
    + (selectors.catalogItemId ? 50 : 0)
    + (selectors.channel !== 'any' ? 20 : 0)
    + (selectors.merchantPlanCode ? 10 : 0)
  );
}

export function getForfeoReserveSelectedScopes(
  selectors: ForfeoReserveCommercialPolicySelectors,
): readonly ForfeoReservePolicyScope[] {
  const scopes: ForfeoReservePolicyScope[] = [];
  if (selectors.resourceId) scopes.push('resource');
  else if (selectors.catalogItemId) scopes.push('service');
  else scopes.push('establishment');
  if (selectors.channel !== 'any') scopes.push('channel');
  if (selectors.merchantPlanCode) scopes.push('merchant_plan');
  return scopes;
}

export function calculateForfeoReservePolicyDeadline(
  startsAt: string,
  windowMinutes: number,
): string | null {
  const start = Date.parse(startsAt);
  if (!Number.isFinite(start) || !Number.isSafeInteger(windowMinutes)) {
    return null;
  }
  return new Date(start - windowMinutes * 60_000).toISOString();
}

function validReference(value: unknown): boolean {
  return value === undefined
    || value === null
    || value === ''
    || (typeof value === 'string' && REFERENCE.test(value));
}

export function validateForfeoReservePolicyResolutionRequest(
  request: ForfeoReservePolicyResolutionRequest,
): readonly string[] {
  const errors: string[] = [];
  if (!request || typeof request !== 'object') return ['invalid_resolution_request'];
  if (!UUID.test(String(request.businessId || ''))) errors.push('invalid_business_id');
  if (!UUID.test(String(request.catalogItemId || ''))) errors.push('invalid_catalog_item_id');
  if (request.resourceId && !UUID.test(request.resourceId)) errors.push('invalid_resource_id');
  if (!isForfeoReserveBookingChannel(request.channel)) errors.push('invalid_channel');
  if (
    request.merchantPlanCode
    && !PLAN_CODE.test(request.merchantPlanCode)
  ) errors.push('invalid_merchant_plan_code');
  if (!Number.isFinite(Date.parse(String(request.startsAt || '')))) {
    errors.push('invalid_starts_at');
  }
  if (
    typeof request.correlationId !== 'string'
    || request.correlationId.trim().length < 8
    || request.correlationId.length > 200
  ) errors.push('invalid_correlation_id');
  if (hasForfeoReserveForbiddenClientFields(request)) {
    errors.push('financial_fields_forbidden');
  }
  return errors;
}

export function validateForfeoReservePolicyCommand(
  command: ForfeoReservePolicyCommand,
): readonly string[] {
  const errors: string[] = [];
  if (!command || typeof command !== 'object') return ['invalid_policy_command'];
  if (!isForfeoReservePolicyCommandKind(command.kind)) errors.push('invalid_command_kind');
  if (!UUID.test(String(command.businessId || ''))) errors.push('invalid_business_id');
  if (!normalizeForfeoReserveIdempotencyKey(command.idempotencyKey)) {
    errors.push('invalid_idempotency_key');
  }
  if (
    typeof command.correlationId !== 'string'
    || command.correlationId.trim().length < 8
    || command.correlationId.length > 200
  ) errors.push('invalid_correlation_id');
  if (hasForfeoReserveForbiddenClientFields(command)) {
    errors.push('financial_fields_forbidden');
  }

  if (command.kind === 'upsert_policy') {
    const policy = command.policy;
    if (!policy) return [...errors, 'policy_required'];
    if (typeof policy.label !== 'string' || policy.label.trim().length < 2 || policy.label.length > 160) {
      errors.push('invalid_policy_label');
    }
    if (!isForfeoReserveConfirmationMode(policy.confirmationMode)) {
      errors.push('invalid_confirmation_mode');
    }
    if (!policy.selectors || !isForfeoReserveBookingChannel(policy.selectors.channel)) {
      errors.push('invalid_selectors');
    } else {
      if (policy.selectors.catalogItemId && !UUID.test(policy.selectors.catalogItemId)) {
        errors.push('invalid_catalog_item_id');
      }
      if (policy.selectors.resourceId && !UUID.test(policy.selectors.resourceId)) {
        errors.push('invalid_resource_id');
      }
      if (policy.selectors.merchantPlanCode && !PLAN_CODE.test(policy.selectors.merchantPlanCode)) {
        errors.push('invalid_merchant_plan_code');
      }
    }
    for (const [name, value] of [
      ['cancellation_window', policy.cancellationWindowMinutes],
      ['modification_window', policy.modificationWindowMinutes],
    ] as const) {
      if (!Number.isSafeInteger(value) || value < 0 || value > 525_600) {
        errors.push(`invalid_${name}`);
      }
    }
    if (!isForfeoReservePolicyEffect(policy.lateCancellationEffect)) {
      errors.push('invalid_late_cancellation_effect');
    }
    if (!isForfeoReservePolicyEffect(policy.noShowEffect)) {
      errors.push('invalid_no_show_effect');
    }
    if (!validReference(policy.financialPolicyReference)) errors.push('invalid_financial_policy_reference');
    if (!validReference(policy.cancellationPolicyReference)) errors.push('invalid_cancellation_policy_reference');
    if (!validReference(policy.creditPolicyReference)) errors.push('invalid_credit_policy_reference');
    if (!Number.isSafeInteger(policy.priority) || policy.priority < 0 || policy.priority > 100_000) {
      errors.push('invalid_priority');
    }
    if (policy.validFrom && !Number.isFinite(Date.parse(policy.validFrom))) errors.push('invalid_valid_from');
    if (policy.validUntil && !Number.isFinite(Date.parse(policy.validUntil))) errors.push('invalid_valid_until');
    if (
      policy.validFrom
      && policy.validUntil
      && Date.parse(policy.validFrom) >= Date.parse(policy.validUntil)
    ) errors.push('invalid_valid_window');
    if (
      forfeoReservePolicyRequiresGiftshop(policy.confirmationMode)
      && !policy.financialPolicyReference
    ) errors.push('financial_policy_reference_required');
  } else {
    if (!UUID.test(String(command.policyId || ''))) errors.push('policy_id_required');
    if (!Number.isSafeInteger(command.expectedRevision) || Number(command.expectedRevision) < 1) {
      errors.push('expected_revision_required');
    }
  }
  return errors;
}

export function assertForfeoReservePolicyAuthorityBoundary(): true {
  if (
    FORFEO_RESERVE_AUTHORITIES.availability !== 'RESERVE'
    || FORFEO_RESERVE_AUTHORITIES.financial !== 'GIFTSHOP'
  ) {
    throw new Error('forfeo_reserve_policy_authority_boundary_invalid');
  }
  return true;
}
